import { TriangleDownIcon } from '@primer/octicons-react';
// The design-system button cannot anchor a SelectPanel: it takes `onPress`, not the `onClick` and
// aria props the panel spreads on its anchor. Primer's own button, small to sit beside the view switch.
import { Button, SelectPanel, type SelectPanelItemInput } from '@primer/react';
import type { DmaMatrixTopic } from '@upright/common/mcp-payloads/dma-matrix';
import { uniqBy } from 'lodash';
import { useMemo, useState } from 'react';

/**
 * The Matters view's topic filter: the ESRS topics under their categories, as on the chat card.
 * Nothing ticked is every topic. Topics with no material matter are listed but cannot be ticked.
 */
export function TopicFilter(props: {
  topics: DmaMatrixTopic[];
  selectableTopicIds: ReadonlySet<string>;
  selectedTopicIds: readonly string[];
  onChange: (topicIds: string[]) => void;
}) {
  const { topics, selectableTopicIds, selectedTopicIds, onChange } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const items = useMemo(
    () =>
      topics.map(topic => ({
        id: topic.topicId,
        text: topic.topicName,
        // The entity-specific topics have no category above them and are their own.
        groupId: topic.categoryId ?? topic.topicId,
        disabled: !selectableTopicIds.has(topic.topicId),
      })),
    [topics, selectableTopicIds],
  );
  const groupMetadata = useMemo(
    () =>
      uniqBy(topics, topic => topic.categoryId ?? topic.topicId).map(topic => ({
        groupId: topic.categoryId ?? topic.topicId,
        header: { title: topic.categoryName ?? topic.topicName, variant: 'subtle' as const },
      })),
    [topics],
  );

  const needle = filter.trim().toLowerCase();
  return (
    <SelectPanel
      title="Filter by topic"
      placeholder="All topics"
      renderAnchor={anchorProps => <Button size="small" trailingAction={TriangleDownIcon} {...anchorProps} />}
      placeholderText="Filter topics"
      open={isOpen}
      onOpenChange={setIsOpen}
      items={needle ? items.filter(item => item.text.toLowerCase().includes(needle)) : items}
      selected={items.filter(item => selectedTopicIds.includes(item.id))}
      onSelectedChange={(selected: SelectPanelItemInput[]) => {
        onChange(selected.map(item => String(item.id)));
      }}
      onFilterChange={setFilter}
      groupMetadata={groupMetadata}
      width="small"
    />
  );
}
